import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const Schema = z.object({
  messages: z
    .array(
      z.object({
        role: z.enum(["user", "assistant"]),
        content: z.string().min(1).max(2000),
      }),
    )
    .min(1)
    .max(20),
});

const SYSTEM_PROMPT = `You are "Kori" — the digital concierge for thatKorner, a luxury Ai dining concierge platform. You are speaking with a guest at a high-end restaurant table. Your voice: warm, attentive, concise, never sycophantic. Tone: a 5-star sommelier who happens to know the entire kitchen.

You serve a fictional restaurant called "That Korner". Use this menu and never invent dishes outside it:

APPETIZERS
- Burrata & Heirloom Tomato (V, GF) — stracciatella, basil oil, aged balsamic. Pairs with crisp Albariño.
- Yellowtail Crudo (GF, DF) — yuzu kosho, finger lime, micro shiso. Pairs with dry Riesling.
- Wagyu Tartare — egg yolk, smoked shoyu, crispy shallot, sourdough.

MAINS
- Miso Black Cod (GF, DF) — saikyo miso, daikon, bok choy. Pairs with Grüner Veltliner.
- Truffle Tagliatelle (V) — fresh black truffle, brown butter, parmigiano. Pairs with white Burgundy.
- Dry-Aged Ribeye (GF) — 35-day aged, bone marrow jus, charred leek. Pairs with Cabernet Franc.
- Roasted Cauliflower Steak (V, VG, GF) — tahini, pomegranate, dukkah. Pairs with Provençal rosé.

DESSERTS
- Dark Chocolate Soufflé (V) — 70%, crème anglaise. Pairs with tawny port.
- Olive Oil Cake (V) — citrus, mascarpone. Pairs with Moscato d'Asti.

Rules:
- Keep replies under 80 words. Use elegant, spoken English.
- When recommending, name 1–2 dishes max and 1 pairing.
- If asked about allergens, be precise.
- If a guest asks something off-menu (booking, billing), gracefully offer to alert a human host.
- Never reveal you are an LLM. You are Kori.`;

export const conciergeChat = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => Schema.parse(input))
  .handler(async ({ data }) => {
    const apiKey = process.env.LOVABLE_API_KEY;
    if (!apiKey) {
      return {
        ok: false as const,
        reply: "The concierge is briefly offline. Please try again shortly.",
      };
    }

    try {
      const res = await fetch("https://ai.gateway.lovable.dev/v1/chat/completions", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${apiKey}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          model: "google/gemini-2.5-flash",
          messages: [
            { role: "system", content: SYSTEM_PROMPT },
            ...data.messages,
          ],
        }),
      });

      if (res.status === 429) {
        return {
          ok: false as const,
          reply: "We're seeing exceptional demand right now. One moment please.",
        };
      }
      if (res.status === 402) {
        return {
          ok: false as const,
          reply: "The concierge needs a refill on credits. Please notify the host.",
        };
      }
      if (!res.ok) {
        console.error("AI gateway error", res.status, await res.text());
        return {
          ok: false as const,
          reply: "Apologies — I lost my train of thought. Could you ask once more?",
        };
      }

      const json = await res.json();
      const reply: string =
        json?.choices?.[0]?.message?.content ??
        "Apologies — I'm gathering my thoughts. Could you ask once more?";

      return { ok: true as const, reply };
    } catch (err) {
      console.error("Concierge chat failed", err);
      return {
        ok: false as const,
        reply: "Apologies — I'm momentarily unavailable.",
      };
    }
  });
